import {
  AbilityProvider,
  AuthProvider,
  createMockAdapter,
  createSupabaseAdapter,
} from "@amazing-care/auth";
import { normalizeError, supabase } from "@amazing-care/supabase";
import { Toaster } from "@amazing-care/ui/sonner";
import { TooltipProvider } from "@amazing-care/ui/tooltip";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { domAnimation, LazyMotion, MotionConfig } from "motion/react";
import { RouterProvider } from "react-router";
import { toast } from "sonner";
import { ErrorBoundary } from "@/components/error-boundary";
import { useThemeEffect } from "@/hooks/use-theme-effect";
import { env, IS_MOCK_DATA } from "@/lib/env";
import { router } from "@/router";
import { useResolvedTheme } from "@/stores/theme.store";

let lastAuthToast = 0;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 60, retry: 1 },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      const pgError = error as { code?: string };
      if (pgError.code === "PGRST301" || pgError.code === "42501") {
        const now = Date.now();
        if (now - lastAuthToast > 5000) {
          lastAuthToast = now;
          toast.error("Session expired. Please sign in again.");
        }
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.silent) return;
      toast.error(normalizeError(error));
    },
    onSuccess: (_data, _variables, _context, mutation) => {
      const successMessage = mutation.meta?.successMessage;
      if (typeof successMessage === "string") toast.success(successMessage);
    },
  }),
});

// Mock mode starts signed in so local development lands straight on the portal.
const authAdapter = IS_MOCK_DATA
  ? createMockAdapter({ role: env.VITE_MOCK_ROLE, signedIn: true })
  : createSupabaseAdapter(supabase, { ssoDomain: env.VITE_SSO_DOMAIN });

function ThemedShell() {
  useThemeEffect();
  const theme = useResolvedTheme();

  return (
    // LazyMotion with `m` components ships only the animation features used,
    // not all of motion. Reduced motion keeps the fades and drops the movement.
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user">
        <RouterProvider router={router} />
        <Toaster position="top-right" theme={theme} />
      </MotionConfig>
    </LazyMotion>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider adapter={authAdapter}>
          <AbilityProvider>
            <TooltipProvider>
              <ThemedShell />
            </TooltipProvider>
          </AbilityProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
